import { useForm } from '@kanda-libs/ks-component-ts';
import {
  ModalsWrapperContext,
  type StringIndexedObject,
} from '@kanda-libs/ks-design-library';
import type { Job } from '@kanda-libs/ks-frontend-services';
import useCreateJobContextValue from 'pages/CreateJob/CreateJob-useCreateJobContextValue';
import useIsExampleJob from 'pages/CreateJob/CreateJob-useIsExampleJob';
import { useCallback, useContext, useMemo } from 'react';
import { getSelectedFinanceRates } from 'utils';

import useSubmitChanges from 'pages/CreateJob/CreateJob-useSubmitChanges';
import { checkJobChangesRequested } from 'utils/Job-functions';

import useIsSubscribed from '../../../../../../hooks/useIsSubscribed';
import useSaveDraft from '../../../../CreateJob-useSaveDraft';
import useSubmitDraft from '../../../../CreateJob-useSubmitDraft';
import useSubmitJob from '../../../../CreateJob-useSubmitJob';
import { BASE_BUTTON_PROPS, MODAL_ID } from './Summary-constants';

const ContainerComponent = ({ children }) => {
  const { data } = useCreateJobContextValue();
  const { showModal } = useContext(ModalsWrapperContext);
  const isExampleJob = useIsExampleJob();

  const company = data?.company;
  const showSubmit = isExampleJob || company?.lifecycle === 'verified';

  const { handleSubmit: submitJob, isSubmitting: jobIsSubmitting } =
    useSubmitJob();
  const { handleSubmit: submitDraft, isSubmitting: draftIsSubmitting } =
    useSubmitDraft();
  const { handleSubmit: submitChanges, isSubmitting: changesIsSubmitting } =
    useSubmitChanges();
  const { isSubmitting: draftIsSaving } = useSaveDraft();

  const changesRequested = useMemo(() => {
    const { job } = data;
    if (!job) return false;
    return checkJobChangesRequested(job);
  }, [data]);

  const handler = useMemo(() => {
    const id = data?.job?.id;
    if (!id) return submitJob;
    if (changesRequested) return submitChanges;
    return submitDraft;
  }, [data, changesRequested, submitJob, submitChanges, submitDraft]);

  const isSubmitting = useMemo(
    () =>
      jobIsSubmitting ||
      changesIsSubmitting ||
      draftIsSaving ||
      draftIsSubmitting,
    [jobIsSubmitting, changesIsSubmitting, draftIsSaving, draftIsSubmitting],
  );

  const { isSubscribed } = useIsSubscribed();

  const showFinishSetUpModal = useCallback(() => {
    showModal(MODAL_ID);
  }, [showModal]);

  const form = useForm({
    mode: 'onBlur',
    defaultValues: {
      ...data,
      job: {
        ...data?.job,
        notes: [''],
      },
    },
  });

  const handleSubmit = useCallback(
    (formValues: StringIndexedObject) => {
      if (!showSubmit) return;
      // DEV_NOTE: When using dot notation, TS throws an error of `job` not
      // existing on type `never` - I can't figure for the life of me why
      // it's throwing this error, but this works - Rob
      // eslint-disable-next-line
      const notes = formValues?.['job']?.['notes'] || null;
      const selectedRates = formValues?.job?.finance_options || undefined;
      const formData = {
        ...data,
        job: {
          ...(data.job as Job),
          finance_options: getSelectedFinanceRates(
            data.company?.finance_rates || [],
            selectedRates,
          ),
          ...(notes ? { notes } : {}),
        },
      };
      handler(formData);
    },
    [data, showSubmit, handler],
  );

  return children({
    isSubmitting,
    nextButtonProps: {
      ...BASE_BUTTON_PROPS,
      disabled: isSubmitting,
      ...(!isSubscribed ? { onClick: showFinishSetUpModal } : { submit: true }),
      className: showSubmit ? '' : 'hidden',
    },
    form,
    onSubmit: handleSubmit,
  });
};

ContainerComponent.displayName = 'CreateJob-Summary-Mobile-container';

export default ContainerComponent;
